import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, OnInit, OnDestroy , signal } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTooltip } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import { MapService } from 'src/app/services/map/map.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { RouteListElement } from 'src/app/models/route-list';
import { SearchedRouteElement } from 'src/app/models/searched-route-element';
import { SearchComponent } from 'src/app/components/search/search.component';
import { DisplayMode } from 'src/app/models/display-mode';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-planning-waypoints',
  templateUrl: './planning-waypoints.component.html',
  styleUrls: ['./planning-waypoints.component.scss'],
  imports: [CommonModule, CdkDropList, CdkDrag, CdkDragHandle, MatTooltip, SearchComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningWaypointsComponent implements OnInit, OnDestroy {
  @Input() navigation: boolean = false;
  @Input() selectedDisplayMode: DisplayMode = DisplayMode.ROUTE;
  public readonly DisplayMode = DisplayMode;
  @Input() routeList: RouteListElement[] = [];
  @Input() failedPlan: boolean = false;
  @Input() maxReachedWaypoint: number = 0;
  @Input() mobileResolution: boolean = false;
  @Input() mobileVisibleRoutePanel: string = 'routepoints';
  @Input() emptySearchInput: boolean = false;
  @Input() isAuthorized: boolean = false;

  @Output() requestOpenAccount = new EventEmitter<void>();
  @Output() requestOpenSettings = new EventEmitter<void>();
  @Output() requestOpenGpsError = new EventEmitter<void>();

  public readonly dragAndDropIconPath = signal<string>('');
  public readonly waypointRemoveIconPath = signal<string>('');
  public readonly waypointSetToLocationIconPath = signal<string>('');
  public readonly addTrackPointIconPath = signal<string>('');
  public readonly homeIconPath = signal<string>('');
  public readonly workIconPath = signal<string>('');

  private skinSubscription!: Subscription;
  private languageSubscription!: Subscription;

  constructor(
    private cdr: ChangeDetectorRef,
    public languageService: LanguageService,
    public settingsService: SettingsService,
    private waypointService: WaypointService,
    private mobileResolutionService: MobileResolutionService,
    private mapService: MapService
  ) {}

  ngOnInit(): void {
    this.skinSubscription = this.settingsService.ObservableMap.subscribe(mapSettings => {
      if (!mapSettings) { return; }
      const skin = mapSettings.Skin;
      this.dragAndDropIconPath.set(`/assets/icons/${skin}/ui/draganddroptrack_icon.svg`);
      this.waypointRemoveIconPath.set(`/assets/icons/${skin}/ui/waypoint_remove.svg`);
      this.waypointSetToLocationIconPath.set(`/assets/icons/${skin}/ui/waypoint_set_to_location.svg`);
      this.addTrackPointIconPath.set(`/assets/icons/${skin}/ui/addtrackpoint_icon.svg`);
      this.homeIconPath.set(`/assets/icons/${skin}/ui/home_icon.svg`);
      this.workIconPath.set(`/assets/icons/${skin}/ui/work_icon.svg`);
    });

    // OnPush: re-render translated labels/tooltips when the language changes.
  }

  ngOnDestroy(): void {
    if (this.skinSubscription) {
      this.skinSubscription.unsubscribe();
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  public addRoutePoint(): void {
    if (!this.emptySearchInput && !this.failedPlan) {
      this.waypointService.addEmptyPlaceholder();
    }
  }

  public addHomeToRoutePoint(): void {
    if (!this.isAuthorized) {
      this.requestOpenAccount.emit();
    } else if (!this.failedPlan) {
      const homeAddress = this.settingsService.getHomeAddress();
      if (homeAddress?.Description) {
        this.waypointService.addEndpoint({
          display_name: homeAddress.Description,
          lat: homeAddress.Location.lat,
          lng: homeAddress.Location.lng,
        });
      } else {
        this.requestOpenSettings.emit();
      }
    }
  }

  public addWorkToRoutePoint(): void {
    if (!this.isAuthorized) {
      this.requestOpenAccount.emit();
    } else if (!this.failedPlan) {
      const workAddress = this.settingsService.getWorkAddress();
      if (workAddress?.Description) {
        this.waypointService.addEndpoint({
          display_name: workAddress.Description,
          lat: workAddress.Location.lat,
          lng: workAddress.Location.lng,
        });
      } else {
        this.requestOpenSettings.emit();
      }
    }
  }

  public isDragAndDrop(): boolean {
    if ((this.routeList[this.routeList.length - 1]?.wp ?? -1) > 0) {
      return true;
    }

    if (this.routeList.length > 1 && (this.routeList[this.routeList.length - 2]?.wp ?? -1) > 0) {
      return true;
    }

    return false;
  }

  public drop(event: CdkDragDrop<RouteListElement[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const reordered = [...this.routeList];
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      this.waypointService.setRouteListAndRebuild(reordered);
    }
  }

  public deletePoint(i: number): void {
    this.waypointService.deleteRouteListEntry(i);
  }

  public setStartCoordsToYourGeolocation(): void {
    const unshiftWaypoints = (this.routeList[0]?.lat === 0 && this.routeList[0]?.lng === 0);
    const result = this.waypointService.setStartToGeolocation(unshiftWaypoints);
    if (result === 'nogps') {
      this.requestOpenGpsError.emit();
    } else {
      const startcoord = this.waypointService.getStartCoordsParams();
      this.mapService.getReverseGeocode(startcoord.lat, startcoord.lng).subscribe({
        next: (geocode) => {
          if (geocode) {
            this.waypointService.setStartDisplayName(geocode);
          }
        }
      });
    }
  }

  public keyPressedHandler(): void {
    this.cdr.detectChanges();
  }

  public waypointChangedHandler(searchData: SearchedRouteElement, i: number): void {
    this.waypointService.setWaypointAtIndex(i, {
      display_name: searchData.display_name,
      lat: searchData.lat,
      lng: searchData.lon,
    });
  }

  public getRouteStartIcon(routeElement: RouteListElement): string {
    return routeElement.wp == -1 ? 'empty' : 'assets/icons/dark/ui/start_icon.svg';
  }

  public getRouteWaypointIcon(routeElement: RouteListElement): string {
    if (routeElement.wp == -1) {
      return 'empty';
    }
    else if (routeElement.csid) {
      return 'assets/icons/default/ui/charge_here_waypoint_list.svg';
    }
    else {
      return 'waypoint';
    }
  }

  public getRouteEndpointIcon(routeElement: RouteListElement): string {
    if (routeElement.wp == -1) {
      return 'empty';
    }
    else if (routeElement.csid) {
      return 'assets/icons/default/ui/charge_here_target_list.svg';
    }
    else {
      return 'assets/icons/dark/ui/target_icon.svg';
    }
  }

}